import React, { useState, useEffect } from "react";
import { useLocation, useParams, Link } from "react-router-dom";
import { receiptUrl as buildReceiptUrl, API_URL } from "../services/api";
import { formatPrice } from "../utils/currency";

// UI strings keyed by language. The customer's chosen language sits in
// localStorage (set by MenuPage's language toggle). We read it once on
// mount -- this is a confirmation screen, no need to react to changes.
const T = {
  en: {
    received: "✅ Order received",
    order_hash: "Order",
    thanks_sent: "Thanks! Your order has been sent to the kitchen.",
    we_bring: "We'll bring it to",
    table: "table",
    summary: "Order summary",
    total: "Total",
    status: "Status",
    status_pending:   "Sent to kitchen",
    status_preparing: "Being prepared",
    status_ready:     "Ready — waiter will bring it",
    status_served:    "Served — enjoy!",
    status_cancelled: "Cancelled",
    // Short labels for the 4-step status track viz at the top of
    // this page. Kept concise so they fit under the dots on mobile.
    step_received:  "Received",
    step_preparing: "Preparing",
    step_ready:     "Ready",
    step_served:    "Delivered",
    rate_after: "⭐ Rate after your meal",
    view_receipt: "🧾 View / print receipt",
    back_to_start: "Back to start",
    rating_hint:
      "The rating will be ready once your order is served — your hotel uses these ratings to thank good waiters.",
  },
  nl: {
    received: "✅ Bestelling ontvangen",
    order_hash: "Bestelling",
    thanks_sent: "Bedankt! Je bestelling is naar de keuken gestuurd.",
    we_bring: "We brengen het naar",
    table: "tafel",
    summary: "Overzicht",
    total: "Totaal",
    status: "Status",
    status_pending:   "Naar de keuken gestuurd",
    status_preparing: "Wordt bereid",
    status_ready:     "Klaar — wordt opgediend",
    status_served:    "Geserveerd — eet smakelijk!",
    status_cancelled: "Geannuleerd",
    step_received:  "Ontvangen",
    step_preparing: "Bereiden",
    step_ready:     "Klaar",
    step_served:    "Geserveerd",
    rate_after: "⭐ Geef na de maaltijd een beoordeling",
    view_receipt: "🧾 Bon bekijken / printen",
    back_to_start: "Terug naar start",
    rating_hint:
      "De beoordeling is beschikbaar zodra je bestelling is geserveerd — het restaurant gebruikt deze om goede medewerkers te belonen.",
  },
  am: {
    received: "✅ ትዕዛዝ ተቀብለናል",
    order_hash: "ትዕዛዝ",
    thanks_sent: "አመሰግናለሁ! ትዕዛዝዎ ወደ ኩሽና ተልኳል።",
    we_bring: "ለእርስዎ እናመጣዋለን",
    table: "ጠረጴዛ",
    summary: "የትዕዛዝ ማጠቃለያ",
    total: "ድምር",
    status: "ሁኔታ",
    status_pending:   "ወደ ኩሽና ተልኳል",
    status_preparing: "በዝግጅት ላይ",
    status_ready:     "ዝግጁ — አስተናጋጁ ያቀርብልዎታል",
    status_served:    "ቀርቧል — ይደሰቱ!",
    status_cancelled: "ተሰርዟል",
    step_received:  "ተቀብለናል",
    step_preparing: "በዝግጅት",
    step_ready:     "ዝግጁ",
    step_served:    "ቀርቧል",
    rate_after: "⭐ ከምግቡ በኋላ ይገምግሙ",
    view_receipt: "🧾 ደረሰኝ ይመልከቱ",
    back_to_start: "ወደ ሜኑ  ይመለሱ",
    rating_hint:
      "ግምገማው ትዕዛዝዎ ሲቀርብ ይዘጋጃል።",
  },
  // Tigrinya — MACHINE DRAFT 2026-06-05; native-speaker review pending
  // (see docs/i18n/ODRA_Translation_Review.xlsx).
  ti: {
    received: "✅ ትእዛዝ ተቐቢልና",
    order_hash: "ትእዛዝ",
    thanks_sent: "የቐንየልና! ትእዛዝኩም ናብ ክሽነ ተላኢኹ ኣሎ።",
    we_bring: "ናብኡ ከነምጽኦ ኢና",
    table: "ጠረጴዛ",
    summary: "ጽማቝ ትእዛዝ",
    total: "ድምር",
    status: "ኩነታት",
    status_pending:   "ናብ ክሽነ ተላኢኹ",
    status_preparing: "ይዳሎ ኣሎ",
    status_ready:     "ድሉው — ኣሳላፊ ከምጽኦ እዩ",
    status_served:    "ቀሪቡ — ብጣዕሚ ብልዑ!",
    status_cancelled: "ተሰሪዙ",
    step_received:  "ተቐቢልና",
    step_preparing: "ይዳሎ",
    step_ready:     "ድሉው",
    step_served:    "ቀሪቡ",
    rate_after: "⭐ ድሕሪ ምግብኹም ገምግሙ",
    view_receipt: "🧾 ቅብሊት ርአ / ሕተም",
    back_to_start: "ናብ መጀመርታ ተመለስ",
    rating_hint:
      "ገምጋም ትእዛዝኩም ምስ ቀረበ ድሉው ይኸውን።",
  },
  // Sidaamu Afoo — MACHINE DRAFT 2026-06-05, LOW CONFIDENCE. Native
  // review required before the Hawassa pilot.
  sid: {
    received: "✅ Hajajo adhinoommo",
    order_hash: "Hajajo",
    thanks_sent: "Galatoomme! Hajajokki kishinete soyamino.",
    we_bring: "Shiqishineemmohe",
    table: "xaraphpheeza",
    summary: "Hajajote xaphishsha",
    total: "Xaphooma",
    status: "Garinni",
    status_pending:   "Kishinete soyamino",
    status_preparing: "Qixxaawanni no",
    status_ready:     "Qixxaawino — tajaajilaanchu abbannohe",
    status_served:    "Shiqqino — sagale baxxine!",
    status_cancelled: "Hunamino",
    step_received:  "Adhinoommo",
    step_preparing: "Qixxaawanni",
    step_ready:     "Qixxaawino",
    step_served:    "Shiqqino",
    rate_after: "⭐ Sagalekki gedensaanni wo'naali",
    view_receipt: "🧾 Nagaashshe la'i",
    back_to_start: "Hanafote higgi",
    rating_hint:
      "Wo'naalshu hajajokki shiqqu gedensaanni qixxaawanno.",
  },
  om: {
    received: "✅ Ajaja fudhanne",
    order_hash: "Ajaja",
    thanks_sent: "Galatoomaa! Ajajni keessan gara waaroo ergameera.",
    we_bring: "Isiniif dhiyessina",
    table: "minjaala",
    summary: "Ajajaa Waliigalaa",
    total: "Ida'ama",
    status: "Haala",
    status_pending:   "Kuushinatti ergameera",
    status_preparing: "Qophaa'aat jira",
    status_ready:     "Qophaa'eera — tajaajilaan(tun) isini dhiyeessa(siti)",
    status_served:    "Dhiyaateera — itti boharaa!",
    status_cancelled: "Haqame",
    step_received:  "Fudhanne",
    step_preparing: "Qophaa'aa",
    step_ready:     "Qophaa'e",
    step_served:    "Dhiyaate",
    rate_after: "⭐ Erga nyaattaniin booda madaalaa",
    view_receipt: "🧾 Nagahee ilaalaa",
    back_to_start: "Gara Meenutti deebi'aa",
    rating_hint:
      "Madaallichi ajajni kee yoo dhihaate qophaa'a.",
  },
};

// Statuses that mean "done one way or another" — we stop polling once
// the order lands on one of these. Saves backend trips for tabs that
// the customer leaves open after their meal.
const TERMINAL_STATUSES = new Set(["served", "cancelled"]);

// Poll interval for live status updates. 12s strikes a balance between
// "the customer sees it move" and "we don't hammer the backend with
// hundreds of /order-info calls per pilot evening." Adjust if needed.
const POLL_INTERVAL_MS = 12000;

// 4-step lifecycle: Received → Preparing → Ready → Delivered. Customer-
// facing version of the dashboard's StatusTrack (components/OrderCard.js).
// Display-only — actual transitions still happen on the staff side via
// the kitchen UI; this is just a visualization for the waiting guest.
const STATUS_STEPS = [
  { key: "received",  labelKey: "step_received"  },
  { key: "preparing", labelKey: "step_preparing" },
  { key: "ready",     labelKey: "step_ready"     },
  { key: "served",    labelKey: "step_served"    },
];

// Map server-side order_status → step index. 'pending' and 'confirmed'
// both surface as step 0 (Received); 'completed' is treated the same as
// 'served' (step 3) so the chart never shows a customer's order as
// "incomplete" after the staff have marked completion.
const STATUS_STEP_INDEX = {
  pending:   0,
  confirmed: 0,
  preparing: 1,
  ready:     2,
  served:    3,
  completed: 3,
};

function CustomerStatusTrack({ status, t }) {
  if (status === "cancelled") {
    return (
      <div className="cust-status-track cust-status-cancelled" role="status">
        ✕ {t("status_cancelled")}
      </div>
    );
  }
  const current = STATUS_STEP_INDEX[status] ?? 0;
  return (
    <div className="cust-status-track" aria-label="Order status">
      {STATUS_STEPS.map((step, i) => {
        const cls =
          i < current ? "cust-status-step done"
          : i === current ? "cust-status-step current"
          : "cust-status-step todo";
        return (
          <React.Fragment key={step.key}>
            <div className={cls}>
              <span className="cust-status-dot">
                {i < current ? "✓" : i + 1}
              </span>
              <span className="cust-status-label">{t(step.labelKey)}</span>
            </div>
            {i < STATUS_STEPS.length - 1 && (
              <div className={"cust-status-connector" + (i < current ? " done" : "")} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function OrderConfirmationPage() {
  // The :orderId route param now carries the order's unguessable public_token.
  const { orderId: token } = useParams();
  const { state } = useLocation();
  const order = state?.order;
  const table = state?.tableInfo;
  // Per-tenant currency from tableInfo (carried over from MenuPage via
  // navigate state). Falls back to ETB for legacy navigation paths.
  const currency = table?.currency || "ETB";
  const receiptUrl = buildReceiptUrl(token);
  const lang = localStorage.getItem("menulink_lang") || "en";
  const t = (k) => (T[lang] && T[lang][k]) || T.en[k] || k;

  // Per-tenant flag from /restaurant-info: when true, poll for live
  // status updates; when false, hide the status line entirely. Default
  // true if missing for safety (most tenants want it on).
  const showCustomerStatus = table?.show_customer_status !== false;

  // Live status state — starts from whatever the order had at submit
  // time (state.order.status). useEffect below polls /public/order-info
  // to update it as the kitchen + waiter advance the order. Friend-test
  // feedback #75: previously the displayed status was frozen forever.
  const [liveStatus, setLiveStatus] = useState(order?.status || "pending");
  const [ratingsEnabled, setRatingsEnabled] = useState(true);
  const [info, setInfo] = useState(null);
  // Venue payment details: prefer the tableInfo carried via navigation;
  // fall back to the polled order-info (e.g. if the guest reloaded the page).
  const pay = table || info || {};
  // Short human-facing order number for the "Order #…" line. From the create
  // response (order.id) or, after a reload, the polled order-info (order_id).
  const displayId = order?.id ?? info?.order_id ?? null;

  useEffect(() => {
    if (!showCustomerStatus || !token) return;
    if (TERMINAL_STATUSES.has(liveStatus)) return;

    let cancelled = false;
    const tick = async () => {
      try {
        const res = await fetch(
          `${API_URL.replace(/\/$/, "")}/public/order-info/${token}`
        );
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (data?.status && data.status !== liveStatus) {
          setLiveStatus(data.status);
        }
      } catch {
        // Network blip — try again on the next tick rather than crashing
        // the page. Customer just sees the last-known status until the
        // next poll succeeds.
      }
    };
    // First tick after the interval (state.order.status from submit is
    // already current); subsequent ticks at POLL_INTERVAL_MS.
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [token, showCustomerStatus, liveStatus]);

  // One-time check: does this venue collect ratings? Starter-tier venues
  // don't, so we hide the rating call-to-action for their guests.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL.replace(/\/$/, "")}/public/order-info/${token}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setInfo(data);
        if (!cancelled && typeof data?.ratings_enabled === "boolean") {
          setRatingsEnabled(data.ratings_enabled);
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Resolve the friendly localised label for the current status, falling
  // back to the raw backend value if we don't have a translation yet.
  const statusLabel =
    T[lang]?.[`status_${liveStatus}`] ||
    T.en[`status_${liveStatus}`] ||
    liveStatus;

  return (
    <div className="page">
      <div className="brand-bar">
        <h1>{t("received")}</h1>
        {/* Show the short human order number when we have it (from the create
            response or the polled order-info); the public_token in the URL is
            long and not meant to be read out. */}
        {displayId != null && (
          <div className="sub">{t("order_hash")} #{displayId}</div>
        )}
      </div>

      <div className="success" style={{ marginTop: 16 }}>
        {t("thanks_sent")}
        {table?.table_number != null && (
          <>
            {" "}
            {t("we_bring")} <b>{t("table")} {table.table_number}</b>.
          </>
        )}
      </div>

      {order && (
        <div className="card" style={{ marginTop: 16 }}>
          <h3 style={{ marginTop: 0 }}>{t("summary")}</h3>
          {order.items.map((it) => (
            <div key={it.id} className="cart-line">
              <span className="name">
                {it.name || `Item #${it.menu_id}`}{" "}
                <span className="muted">× {it.quantity}</span>
              </span>
              <span>
                {formatPrice((it.price || 0) * it.quantity, currency)}
              </span>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, marginTop: 8 }}>
            <span>{t("total")}</span>
            <span>{formatPrice(order.total || 0, currency)}</span>
          </div>
          {showCustomerStatus && (
            <div style={{ marginTop: 16 }}>
              <CustomerStatusTrack status={liveStatus} t={t} />
              {/* Long-form current-status sentence below the viz — gives
                  more context ("Ready — waiter will bring it") than the
                  short dot label, and stays useful for visually-impaired
                  guests who rely on the text rather than the dots. */}
              <p className="muted" style={{ marginTop: 8, textAlign: "center", fontSize: ".9rem" }}>
                {statusLabel}
              </p>
            </div>
          )}
        </div>
      )}

      {(pay.telebirr_number || pay.cbe_birr_number || pay.payment_qr_url) && (
        <div className="card" style={{ marginTop: 16 }}>
          <h3 style={{ marginTop: 0 }}>💳 {lang === "am" ? "እንዴት እንደሚከፍሉ" : lang === "om" ? "Akkaataa kaffaltii" : "How to pay"}</h3>
          {pay.payment_qr_url ? (
            <div style={{ textAlign: "center" }}>
              <div className="muted" style={{ fontSize: ".85rem", marginBottom: 8 }}>
                {lang === "am"
                  ? "የቴሌብር/ሲቢኢ መተግበሪያዎን ይክፈቱ፣ ይህን ኮድ ይቃኙ እና ይክፈሉ።"
                  : lang === "om"
                  ? "Telebirr/CBE keessan banaa, koodii kana skaanii godhaatii kaffalaa."
                  : "Open your Telebirr / CBE app, scan this code, and pay."}
              </div>
              <img src={pay.payment_qr_url} alt="Payment QR"
                style={{ width: 200, maxWidth: "70%", borderRadius: 8, border: "1px solid #cfe6e6" }}
                onError={(e) => { e.currentTarget.style.display = "none"; }} />
            </div>
          ) : (
            <>
              <div className="muted" style={{ fontSize: ".85rem", marginBottom: 8 }}>
                {lang === "am"
                  ? "ለማስተላለፍ ከታች ያለውን ቁጥር ይጠቀሙ።"
                  : lang === "om"
                  ? "Daddabarsuuf lakkoofsa gadii fayyadami."
                  : "Transfer using the number below."}
              </div>
              {pay.telebirr_number && (
                <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}><span>Telebirr</span><b>{pay.telebirr_number}</b></div>
              )}
              {pay.cbe_birr_number && (
                <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}><span>CBE Birr</span><b>{pay.cbe_birr_number}</b></div>
              )}
            </>
          )}
          <p className="muted" style={{ fontSize: ".8rem", marginTop: 10, textAlign: "center" }}>
            {lang === "am"
              ? "ሲዘጋጁ ይክፈሉና ማረጋገጫዎን ለአስተናጋጁ ያሳዩ።"
              : lang === "om"
              ? "Yeroo qophooftan kaffalaatii ragaa keessan tajaajilaadhaaf agarsiisaa."
              : "When you're ready, pay and show your confirmation to the waiter."}
          </p>
        </div>
      )}

      <div style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
        {ratingsEnabled && (
        <Link className="btn" to={`/rate/${token}`}>
          {t("rate_after")}
        </Link>
        )}
        <a
          className="btn secondary"
          href={receiptUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("view_receipt")}
        </a>
        <Link className="btn ghost" to="/">
          {t("back_to_start")}
        </Link>
      </div>
      {ratingsEnabled && (
      <p className="muted" style={{ fontSize: ".85rem", marginTop: 12 }}>
        {t("rating_hint")}
      </p>
      )}
    </div>
  );
}
